import React, { useEffect, useState } from 'react';
import { toLower } from 'lodash';
import { useLocale } from 'locale';
import { ShowAt, useScreenBreakpoint } from 'consts/breakpoints';
import { Button } from 'ds/components/button';
import { createPortal } from 'utils/universalPortal';
import { STICKY_ROOT } from 'consts/rootNodes';
import { IDeveloperProfile } from 'utils/entities';
import { DeveloperLeadVariables } from 'store/sagas/apiService/types';
import PhoneButton from 'components/phone-button';
import ContactModal from './ContactModal';
import { SuccessModal } from './SuccessModal';
import * as Styled from './styled';
import withEvents from 'analytics/withEvents';
import { noop } from 'lodash';


interface ContactProps {
  developer: IDeveloperProfile;
  initialValues: DeveloperLeadVariables;
  onContactDeveloper: (data: DeveloperLeadVariables) => void;
  onContactDeveloperSuccess?: (data: DeveloperLeadVariables, leadId: string) => void;
  submitting: boolean;
  submitted: boolean;
  isSmallBtn?: boolean;
  onModalOpen?: () => void;
  onModalClose?: () => void;
  onMobilePhoneNumberClick?: () => void;
  submittedLeadId?: string;
}

const Contact: React.FC<ContactProps> = (props) => {
  const {
    developer,
    initialValues,
    isSmallBtn,
    onContactDeveloper,
    submitting,
    submitted,
    onModalOpen,
    onModalClose,
    onMobilePhoneNumberClick,
    onContactDeveloperSuccess,
    submittedLeadId,
  } = props;
  const { phoneNumber, name, logoUrl } = developer;
  const [ isModalOpen, setIsModalOpen ] = useState(false);
  const [ isSuccessOpen, setIsSuccessOpen ] = useState(false);
  const [ eventData, setEventData ] = useState(null);
  const { t } = useLocale();
  const screen = useScreenBreakpoint();
  const isMobile = screen === 1;
  const isTablet = screen === 2;
  const isDesktop = screen > 2;

  const buttonSize = isDesktop || isSmallBtn || isMobile
    ? 'large'
    : isTablet
      ? 'extraXLarge' : 'extraLarge';

  const closeModal = () => {
    setIsModalOpen(false);
    onModalClose();
  };
  const openModal = () => {
    onModalOpen();
    setIsModalOpen(true);
  };

  useEffect(() => {
    if (submitted) {
      setIsSuccessOpen(true);
      onContactDeveloperSuccess(eventData, submittedLeadId);
      setEventData(null);
    }
  }, [ submitted ]);
  const onSuccessClose = () => setIsSuccessOpen(false);

  const handleContactDeveloper = (data: DeveloperLeadVariables) => {
    setEventData(data);
    onContactDeveloper(data);
  };

  const contactButton = (
    <Button
      size={buttonSize}
      disabled={submitted || submitting}
      onClick={openModal}
      fullWidth={isMobile}
      mode={!isMobile || submitted ? 'primary' : 'ghost'}
      data-auto="developer-contact-button"
    >
      {t('developerPage.contactButton', { submitted, isMobile })}
    </Button>
  );

  return (
    <>
      <ShowAt from={2}>
        {contactButton}
      </ShowAt>
      <ShowAt at={1}>
        {createPortal(
          <Styled.MobileContactWrapper>
            {phoneNumber ? (
              <>
                <Styled.MobileContactButtonWrapper>
                  {contactButton}
                </Styled.MobileContactButtonWrapper>
                <Styled.MobileContactButtonWrapper onClick={onMobilePhoneNumberClick}>
                  <PhoneButton
                    exposePhone={false}
                    phone={phoneNumber}
                    fullWidth
                    size="large"
                    mode="primary"
                    label={t('developerPage.contactPhoneButton')}
                  />
                </Styled.MobileContactButtonWrapper>
              </>
            ) : contactButton }
          </Styled.MobileContactWrapper>
          , STICKY_ROOT)}
      </ShowAt>
      {isModalOpen ? (
        <ContactModal
          name={name}
          logoUrl={logoUrl}
          phone={phoneNumber}
          onClose={closeModal}
          initialValues={initialValues}
          contact={handleContactDeveloper}
          submitting={submitting}
          submitted={submitted}
          developer={developer}
        />
      ) : null}
      <ShowAt at={1}>
        {isSuccessOpen ? (
          <SuccessModal onClose={onSuccessClose} />
        ) : null}
      </ShowAt>
    </>
  );
};

Contact.defaultProps = {
  onModalOpen: noop,
  onModalClose: noop,
  onContactDeveloper: noop,
  onMobilePhoneNumberClick: noop,
  onContactDeveloperSuccess: noop,
};

export default withEvents<ContactProps>((sendEvent, props) => {
  const developer = {
    id: props.developer.id,
    integration_level: toLower(props.developer.integrationLevel),
    name: props.developer.name,
  };
  return ({
    onModalOpen() {
      sendEvent('developer_contact_form_open', 'developer', { developer });
    },
    onModalClose() {
      sendEvent('developer_contact_form_close', 'developer', { developer });
    },
    onContactDeveloper(data: DeveloperLeadVariables) {
      sendEvent('developer_contact_form_submit', 'developer', {
        event: {
          contact_details: {
            email: data.email,
            full_name: data.name,
            phone: data.phone,
          },
        },
        developer,
      });
    },
    onContactDeveloperSuccess(data, leadId) {
      sendEvent('developer_contact_form_submit_success', 'developer', {
        event: {
          contact_details: {
            email: data.email,
            full_name: data.name,
            phone: data.phone,
          },
          lead_id: leadId,
        },
      });
    },
    onMobilePhoneNumberClick() {
      sendEvent('developer_lead_call_submit', 'developer', {
        developer,
        rtbData: {
          phone: props.developer && props.developer.phoneNumber,
        },
      });
    },
  });
})(Contact);
